
import { motion } from 'framer-motion';
import {
  Brain,
  Users,
  Trophy,
  Zap,
  Target,
  ArrowRight,
  BookOpen,
  Star,
  Crown,
  Sparkles
} from 'lucide-react';
import Link from '../Link';
import { useEffect, useState } from 'react';
import { getAllUsers } from '../../api/authService';

const QuizSection = () => {
  const [allUsers, setAllUsers] = useState([])
  const [comments, setComments] = useState([]);

  useEffect(() => {
    const fetchUsers = async () => {
      const users = await getAllUsers();
      if (users) {
        setAllUsers(users);
      }
    };

    fetchUsers();
  }, [])





  return (
    <div className="min-h-screen flex justify-center items-center px-4 py-16 relative overflow-hidden">
      {/* Dynamic Animated Background */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-amber-900 via-orange-900 to-red-900"
        animate={{
          background: [
            "linear-gradient(to bottom right, #78350f, #9a3412, #991b1b)",
            "linear-gradient(to bottom right, #f59e0b, #ea580c, #dc2626)",
            "linear-gradient(to bottom right, #78350f, #9a3412, #991b1b)"
          ]
        }}
        transition={{ duration: 8, repeat: Infinity }}
      >
        <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/159711/books-bookstore-book-reading-159711.jpeg')] bg-cover bg-center opacity-20"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
      </motion.div>

      {/* Floating Elements */}
      <motion.div
        animate={{
          y: [-25, 25, -25],
          rotate: [0, 15, 0, -15, 0]
        }}
        transition={{ duration: 10, repeat: Infinity }}
        className="absolute top-16 left-12 hidden lg:block"
      >
        <BookOpen className="h-16 w-16 text-amber-400/40" />
      </motion.div>

      <motion.div
        animate={{
          y: [20, -20, 20],
          rotate: [0, -12, 0, 12, 0]
        }}
        transition={{ duration: 7, repeat: Infinity, delay: 1 }}
        className="absolute top-24 right-20 hidden lg:block"
      >
        <Crown className="h-12 w-12 text-yellow-400/50" />
      </motion.div>

      <motion.div
        animate={{
          y: [-15, 15, -15],
          x: [-8, 8, -8]
        }}
        transition={{ duration: 6, repeat: Infinity, delay: 2 }}
        className="absolute bottom-28 left-16 hidden lg:block"
      >
        <Trophy className="h-14 w-14 text-orange-400/40" />
      </motion.div>

      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 180, 360]
        }}
        transition={{ duration: 12, repeat: Infinity, delay: 3 }}
        className="absolute bottom-20 right-24 hidden lg:block"
      >
        <Sparkles className="h-10 w-10 text-amber-300/50" />
      </motion.div>

      <motion.section
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="relative z-10 mx-auto max-w-7xl w-full"
      >
        <motion.div
          className="bg-gradient-to-r from-black/20 to-black/10 backdrop-blur-xl rounded-3xl p-8 md:p-12 text-white shadow-2xl border border-white/20 overflow-hidden relative"
          whileHover={{ scale: 1.02 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          {/* Background Patterns */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-amber-400 to-orange-400 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-br from-yellow-400 to-red-400 rounded-full blur-3xl"></div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              {/* Header */}
              <div className="flex items-center mb-6">
                <motion.div
                  animate={{
                    rotate: [0, 10, 0, -10, 0],
                    scale: [1, 1.1, 1]
                  }}
                  transition={{ duration: 4, repeat: Infinity }}
                  className="bg-gradient-to-r from-amber-400 to-orange-400 p-4 rounded-2xl shadow-lg mr-4"
                >
                  <Brain className="h-12 w-12 text-white" />
                </motion.div>
                <div>
                  <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-amber-300 to-yellow-300 bg-clip-text text-transparent">
                    Weekly Growth Game
                  </h2>
                  <motion.div
                    animate={{ opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="flex items-center mt-2"
                  >
                    <Zap className="h-5 w-5 text-yellow-400 mr-2" />
                    <span className="text-amber-200 font-medium">Challenge Your Faith Knowledge</span>
                  </motion.div>
                </div>
              </div>

              {/* Description */}
              <motion.p
                className="text-xl md:text-2xl mb-8 text-amber-100 leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                Test your biblical knowledge and track your spiritual growth journey with our interactive weekly game
              </motion.p>

              {/* Stats */}
              <motion.div
                className="grid grid-cols-2 gap-6 mb-8"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
              >
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20">
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    <Users className="h-8 w-8 text-amber-300 mx-auto mb-3" />
                  </motion.div>
                  <div className="text-2xl font-bold text-white mb-1">{allUsers.length}+</div>
                  <div className="text-amber-200 text-sm">Active Members</div>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20">
                  <motion.div
                    animate={{ rotate: [0, 360] }}
                    transition={{ duration: 4, repeat: Infinity }}
                  >
                    <Target className="h-8 w-8 text-yellow-300 mx-auto mb-3" />
                  </motion.div>
                  <div className="text-2xl font-bold text-white mb-1">15</div>
                  <div className="text-amber-200 text-sm">Rounds</div>
                </div>
              </motion.div>

              {/* Additional Info */}
              <motion.div
                className="flex flex-wrap gap-4 mb-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
              >
                <div className="flex items-center bg-black/20 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20">
                  <Star className="h-4 w-4 text-yellow-400 mr-2" fill="currentColor" />
                  <span className="text-amber-100 text-sm">Weekly Challenges</span>
                </div>
                <div className="flex items-center bg-black/20 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20">
                  <Trophy className="h-4 w-4 text-amber-400 mr-2" />
                  <span className="text-amber-100 text-sm">Progress Tracking</span>
                </div>
              </motion.div>

              {/* CTA Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/quiz"
                  className="inline-flex items-center bg-gradient-to-r from-amber-400 via-orange-400 to-red-400 text-black px-10 py-5 rounded-full font-bold text-lg shadow-2xl hover:shadow-amber-500/25 transition-all duration-300 group"
                >
                  <span>Play to Win</span>
                  <motion.div
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="ml-3"
                  >
                    <ArrowRight className="h-6 w-6 group-hover:translate-x-1 transition-transform" />
                  </motion.div>
                </Link>
              </motion.div>
            </motion.div>

            {/* Right Visual Content */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
              className="hidden lg:block"
            >
              <div className="relative">
                {/* Main Quiz Card */}
                <motion.div
                  animate={{
                    y: [-10, 10, -10],
                    rotateY: [0, 5, 0, -5, 0]
                  }}
                  transition={{ duration: 6, repeat: Infinity }}
                  className="bg-white/15 backdrop-blur-lg rounded-3xl p-8 border border-white/30 shadow-2xl"
                >
                  <div className="text-center mb-6">
                    <motion.div
                      animate={{ rotate: [0, 360] }}
                      transition={{ duration: 8, repeat: Infinity }}
                      className="inline-block"
                    >
                      <div className="bg-gradient-to-r from-amber-400 to-orange-400 p-6 rounded-full shadow-lg">
                        <Brain className="h-16 w-16 text-white" />
                      </div>
                    </motion.div>
                  </div>

                  <h3 className="text-2xl font-bold text-center mb-6 text-white">
                    This Week's Challenge
                  </h3>

                  <div className="space-y-4">
                    <div className="bg-white/10 rounded-xl p-4 border border-white/20">
                      <p className="text-amber-100 text-sm mb-2">Round 1 of 15</p>
                      <p className="text-white font-medium">
                        "Which book of the Bible contains the verse 'For I know the plans I have for you'?"
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      {['Jeremiah', 'Isaiah', 'Psalms', 'Proverbs'].map((option, index) => (
                        <motion.div
                          key={option}
                          whileHover={{ scale: 1.05 }}
                          className="bg-white/10 hover:bg-white/20 rounded-lg p-3 text-center text-white text-sm font-medium cursor-pointer border border-white/20 transition-all"
                        >
                          {option}
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </motion.div>

                {/* Floating Stats */}
                <motion.div
                  animate={{
                    y: [0, -10, 0],
                    x: [0, 5, 0]
                  }}
                  transition={{ duration: 4, repeat: Infinity, delay: 1 }}
                  className="absolute -top-4 -right-4 bg-gradient-to-r from-green-400 to-emerald-400 rounded-2xl p-4 shadow-lg"
                >
                  <div className="text-center">
                    <Trophy className="h-6 w-6 text-white mx-auto mb-1" />
                    <div className="text-white font-bold text-sm">92%</div>
                    <div className="text-green-100 text-xs">Success Rate</div>
                  </div>
                </motion.div>

                <motion.div
                  animate={{
                    y: [0, 10, 0],
                    x: [0, -5, 0]
                  }}
                  transition={{ duration: 5, repeat: Infinity, delay: 2 }}
                  className="absolute -bottom-4 -left-4 bg-gradient-to-r from-purple-400 to-pink-400 rounded-2xl p-4 shadow-lg"
                >
                  <div className="text-center">
                    <Star className="h-6 w-6 text-white mx-auto mb-1" fill="currentColor" />
                    <div className="text-white font-bold text-sm">4.9</div>
                    <div className="text-purple-100 text-xs">Rating</div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* Bottom Call to Action */}
          <motion.div
            className="text-center mt-12 pt-8 border-t border-white/20"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4 }}
          >
            <motion.p
              className="text-amber-200 mb-4 flex items-center justify-center text-lg"
              animate={{ opacity: [0.7, 1, 0.7] }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <Sparkles className="h-5 w-5 mr-2" />
              Join thousands growing in faith through interactive learning
              <Sparkles className="h-5 w-5 ml-2" />
            </motion.p>
          </motion.div>
        </motion.div>
      </motion.section>
    </div>
  );
};

export default QuizSection;